import { Building2, Loader2, Search, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { type AccountContact, accountApi } from '@/api/commercial/accounts';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';

interface GroupAccount {
  id: string;
  name?: string;
  social_name?: string;
  document: { value: string; type: string };
}

interface SearchContactDialogProps {
  open: boolean;
  onClose: () => void;
  economicGroupId: string;
  currentAccountId: string;
  existingContacts?: { name: string }[];
  onImport: (contacts: Omit<AccountContact, 'id'>[]) => void;
}

const formatDocument = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  }
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
};

const formatPhone = (value: string) => {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return digits.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
};

export function SearchContactDialog({
  open,
  onClose,
  economicGroupId,
  currentAccountId,
  existingContacts,
  onImport,
}: SearchContactDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [groupContacts, setGroupContacts] = useState<
    Array<{ account: GroupAccount; contacts: AccountContact[] }>
  >([]);
  const [selectedContactIds, setSelectedContactIds] = useState<Set<string>>(new Set());

  const contactKey = (name: string, items: { contact: string }[]) =>
    `${name.trim().toLowerCase()}::${[...items.map((i) => i.contact)].sort().join(',')}`;

  const existingContactKeys = new Set(
    (existingContacts || []).map((c) =>
      contactKey(c.name, 'contactItems' in c ? (c as AccountContact).contactItems || [] : []),
    ),
  );
  const isAlreadyImported = (contact: AccountContact) =>
    existingContactKeys.has(contactKey(contact.name, contact.contactItems || []));

  const loadGroupContacts = useCallback(async () => {
    setIsLoading(true);
    try {
      // Find all accounts in the same economic group
      const accountsResult = await accountApi.findMany({
        limit: 100,
      });

      // Filter accounts in the same economic group, excluding current account
      const groupAccounts = (accountsResult.docs || []).filter(
        (a) => a.economicGroupId === economicGroupId && a.id !== currentAccountId,
      );

      // Fetch contacts for each account
      const contactsByAccount = await Promise.all(
        groupAccounts.map(async (account) => {
          const contacts = await accountApi.findContacts(account.id);
          return { account: account as GroupAccount, contacts };
        }),
      );

      // Only keep accounts that have contacts
      setGroupContacts(contactsByAccount.filter((item) => item.contacts.length > 0));
    } catch {
      setGroupContacts([]);
    } finally {
      setIsLoading(false);
    }
  }, [economicGroupId, currentAccountId]);

  useEffect(() => {
    if (open) {
      setSelectedContactIds(new Set());
      loadGroupContacts();
    }
  }, [open, loadGroupContacts]);

  const toggleContact = (contactId: string) => {
    setSelectedContactIds((prev) => {
      const next = new Set(prev);
      if (next.has(contactId)) {
        next.delete(contactId);
      } else {
        next.add(contactId);
      }
      return next;
    });
  };

  const handleImport = () => {
    const selectedContacts: Omit<AccountContact, 'id'>[] = [];

    for (const group of groupContacts) {
      for (const contact of group.contacts) {
        if (selectedContactIds.has(contact.id)) {
          selectedContacts.push({
            accountId: currentAccountId,
            name: contact.name,
            contractEnabled: contact.contractEnabled ?? false,
            positionOrRelation: contact.positionOrRelation || '',
            taxId: contact.taxId || undefined,
            contactFor: contact.contactFor || [],
            contactItems: (contact.contactItems || []).map((item) => ({
              ...item,
              preferredContact: item.preferredContact || {},
            })),
          });
        }
      }
    }

    onImport(selectedContacts);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Buscar Contato no Grupo Econômico
          </DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">
              Buscando contatos do grupo...
            </span>
          </div>
        ) : groupContacts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <UserPlus className="h-10 w-10 text-muted-foreground/50 mb-3" />
            <p className="text-sm text-muted-foreground">
              Nenhum contato encontrado nas empresas do grupo econômico.
            </p>
          </div>
        ) : (
          <ScrollArea className="max-h-[50vh]">
            <div className="space-y-6">
              {groupContacts.map((group) => (
                <div key={group.account.id} className="space-y-2">
                  <div className="flex items-center gap-2 px-1">
                    <Building2 className="h-4 w-4 text-primary" />
                    <span className="font-medium text-sm">
                      {group.account.social_name || group.account.name}
                    </span>
                    <span className="text-xs text-muted-foreground font-mono">
                      {formatDocument(group.account.document.value)}
                    </span>
                    <Badge variant="secondary" className="text-xs">
                      {group.contacts.length}
                    </Badge>
                  </div>

                  <div className="space-y-1.5">
                    {group.contacts.map((contact) => {
                      const alreadyExists = isAlreadyImported(contact);
                      return (
                        <label
                          key={contact.id}
                          htmlFor={`contact-${contact.id}`}
                          className={`flex items-center gap-3 p-3 border rounded-lg transition-colors ${
                            alreadyExists
                              ? 'opacity-50 cursor-not-allowed'
                              : 'hover:bg-accent/50 cursor-pointer'
                          }`}
                        >
                          <Checkbox
                            id={`contact-${contact.id}`}
                            checked={selectedContactIds.has(contact.id)}
                            onCheckedChange={() => !alreadyExists && toggleContact(contact.id)}
                            disabled={alreadyExists}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm">{contact.name}</p>
                            <p className="text-xs text-muted-foreground">
                              {[
                                contact.positionOrRelation,
                                contact.contactItems
                                  ?.map((i) =>
                                    i.type === 'Email' ? i.contact : formatPhone(i.contact),
                                  )
                                  .join(', '),
                              ]
                                .filter(Boolean)
                                .join(' · ')}
                            </p>
                            {alreadyExists && (
                              <p className="text-xs text-amber-600 mt-0.5">Contato já importado</p>
                            )}
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </ScrollArea>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={handleImport} disabled={selectedContactIds.size === 0}>
            <UserPlus className="h-4 w-4 mr-1" />
            Importar {selectedContactIds.size > 0 ? `(${selectedContactIds.size})` : ''}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
